import { Button } from '@crowdfpl/ui-web/button';
import { Dialog, DialogContent } from '@crowdfpl/ui-web/dialog';
import { Input } from '@crowdfpl/ui-web/input';
import { Users } from 'lucide-react';
import { useState, useEffect, FormEvent } from 'react';
import { useUpdateFplId } from '@/hooks/data/profiles';

interface FplIdPromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function FplIdPromptModal({ open, onOpenChange, onSuccess }: FplIdPromptModalProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const updateFplId = useUpdateFplId({ silent: true });

  useEffect(() => {
    if (!open) {
      setValue('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setError('Please enter your FPL ID');
      return;
    }

    const numValue = parseInt(trimmedValue, 10);
    if (isNaN(numValue) || numValue < 1 || numValue > 2147483647) {
      setError('Please enter a valid FPL ID (1–2147483647)');
      return;
    }

    try {
      await updateFplId.mutateAsync({ fpl_id: numValue });
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const message = (err as { message?: string })?.message;
      setError(message || 'Failed to save. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md bg-background border-border rounded-2xl p-6">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-foreground/10 flex items-center justify-center shrink-0">
              <Users className="h-5 w-5 text-foreground" />
            </div>
            <h2 className="text-lg font-bold text-foreground">Link your FPL Team</h2>
          </div>

          <p className="text-sm text-muted-foreground">
            Enter your FPL ID to filter duels by your squad players. Find it at fantasy.premierleague.com → Points
            → URL contains your ID.
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="number"
              inputMode="numeric"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. 1234567"
              className="h-10"
              disabled={updateFplId.isPending}
              min={1}
              max={2147483647}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button
              type="submit"
              className="w-full h-10"
              disabled={updateFplId.isPending || !value.trim()}
            >
              {updateFplId.isPending ? 'Saving...' : 'Save FPL ID'}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
